import React, { useState } from "react";
import BuildingDestinationField from "../../shared/building-destination";
import MessageBox from "../../shared/form-message-box";
import FormResult from "../../shared/form-result";

declare global {
	interface Window {
		gextLiabilityForm: {
			ajaxUrl: string;
			nonce: string;
			cgvUrl: string;
			analyseFormUrl: string;
			appointmentUrl: string;
		};
	}
}

interface FormData {
	address: string;
	total_surface: string;
	destination_immeuble: string;
	has_large_storage_or_offices: string;
	company: string;
	fullname: string;
	email: string;
	phone: string;
	pdl_or_siren: string;
	poste: string;
	message: string;
}

export default function LiabilityForm() {
	const gextLiabilityForm = window.gextLiabilityForm;
	const [destinationImmeuble, setDestinationImmeuble] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const [subjectTo, setSubjectTo] = useState(false);
	const [submittedData, setSubmittedData] = useState<FormData | null>(null);

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setIsLoading(true);
		const formData = new FormData(e.currentTarget);

		// Stocker les données du formulaire pour la redirection
		const savedData: FormData = {
			address: (formData.get("address") as string) || "",
			total_surface: (formData.get("total_surface") as string) || "",
			destination_immeuble:
				(formData.get("destination_immeuble") as string) || "",
			has_large_storage_or_offices:
				(formData.get("has_large_storage_or_offices") as string) || "",
			company: (formData.get("company") as string) || "",
			fullname: (formData.get("fullname") as string) || "",
			email: (formData.get("email") as string) || "",
			phone: (formData.get("phone") as string) || "",
			pdl_or_siren: (formData.get("pdl_or_siren") as string) || "",
			poste: (formData.get("poste") as string) || "",
			message: (formData.get("message") as string) || "",
		};
		setSubmittedData(savedData);

		formData.append("action", "gext_liability_form_submit");
		formData.append("nonce", gextLiabilityForm.nonce);

		try {
			const response = await fetch(gextLiabilityForm.ajaxUrl, {
				method: "POST",
				body: formData,
			});

			const result = await response.json();

			if (result.success) {
				// Succès : afficher le message et masquer le formulaire
				setIsSuccess(true);
				setSubjectTo(result.data.subject_to);
				setMessage(result.data.message);
			} else {
				// Erreur : afficher le message d'erreur
				setIsSuccess(false);
				setMessage(result.data.message || "Une erreur est survenue.");
			}
		} catch (error) {
			// Erreur réseau
			setIsSuccess(false);
			setMessage(
				"Une erreur de connexion est survenue. Veuillez réessayer.",
			);
		} finally {
			setIsLoading(false);
		}
	};

	const buildAnalyseFormUrl = (): string => {
		if (!gextLiabilityForm.analyseFormUrl || !submittedData) {
			return gextLiabilityForm.analyseFormUrl || "#";
		}

		const params = new URLSearchParams();
		if (submittedData.address) params.set("address", submittedData.address);
		if (submittedData.total_surface)
			params.set("surface", submittedData.total_surface);
		if (submittedData.destination_immeuble)
			params.set(
				"destination_immeuble",
				submittedData.destination_immeuble,
			);
		if (submittedData.has_large_storage_or_offices)
			params.set(
				"has_large_storage_or_offices",
				submittedData.has_large_storage_or_offices,
			);
		if (submittedData.company) params.set("company", submittedData.company);
		if (submittedData.fullname)
			params.set("fullname", submittedData.fullname);
		if (submittedData.email) params.set("email", submittedData.email);
		if (submittedData.phone) params.set("phone", submittedData.phone);
		if (submittedData.pdl_or_siren)
			params.set("pdl_or_siren", submittedData.pdl_or_siren);
		if (submittedData.poste) params.set("poste", submittedData.poste);
		if (submittedData.message) params.set("message", submittedData.message);

		const separator = gextLiabilityForm.analyseFormUrl.includes("?")
			? "&"
			: "?";
		return `${
			gextLiabilityForm.analyseFormUrl
		}${separator}${params.toString()}`;
	};

	const handleAnalyseFormClick = () => {
		window.location.href = buildAnalyseFormUrl();
	};

	const handleAppointmentClick = () => {
		if (gextLiabilityForm.appointmentUrl) {
			window.open(
				gextLiabilityForm.appointmentUrl,
				"_blank",
				"noopener,noreferrer",
			);
		}
	};

	return (
		<>
			{!isSuccess && (
				<>
					<MessageBox
						message={message}
						isSuccess={isSuccess}
						isLoading={isLoading}
					/>
					<form
						className="gext-form__form gext-form"
						method="post"
						onSubmit={onSubmit}
					>
						<div className="gext-form__fields-grid">
							{/* 1. Adresse principale */}
							<div className="gext-form__field">
								<label htmlFor="gext-address">
									Adresse principale{" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="gext-address"
									name="address"
									required
								/>
							</div>

							{/* 2. Surface totale */}
							<div className="gext-form__field">
								<label htmlFor="gext-total-surface">
									Surface totale en m²{" "}
									<span className="required">*</span>
								</label>
								<input
									type="number"
									id="gext-total-surface"
									name="total_surface"
									required
									min="0"
									step="1"
								/>
							</div>

							{/* 3. Usage affecté à l'immeuble */}
							<BuildingDestinationField
								destinationImmeuble={destinationImmeuble}
								setDestinationImmeuble={setDestinationImmeuble}
							/>

							{/* 4. Email */}
							<div className="gext-form__field">
								<label htmlFor="gext-email">
									Email <span className="required">*</span>
								</label>
								<input
									type="email"
									id="gext-email"
									name="email"
									required
								/>
							</div>

							{/* 5. Téléphone */}
							<div className="gext-form__field">
								<label htmlFor="gext-phone">
									Numéro de téléphone{" "}
									<span className="required">*</span>
								</label>
								<input
									type="tel"
									id="gext-phone"
									name="phone"
									required
								/>
							</div>

							{/* 6. Entreprise */}
							<div className="gext-form__field">
								<label htmlFor="gext-company">
									Entreprise{" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="gext-company"
									name="company"
									required
								/>
							</div>

							{/* 7. Nom complet */}
							<div className="gext-form__field">
								<label htmlFor="gext-fullname">
									Nom complet{" "}
									<span className="required">*</span>
								</label>
								<input
									type="text"
									id="gext-fullname"
									name="fullname"
									required
								/>
							</div>

							{/* 9. PDL ou SIREN (optionnel) */}
							<div className="gext-form__field">
								<label htmlFor="gext-pdl-or-siren">
									SIREN ou Point De Livraison Enedis (PDL)
								</label>
								<input
									type="text"
									id="gext-pdl-or-siren"
									name="pdl_or_siren"
									pattern="\d{9}|\d{14}"
									title="Entrez un SIREN (9 chiffres) ou un PDL (14 chiffres)"
									placeholder="Ex: 123456789 ou 12345678901234"
								/>
								<p className="gext-form__field__help">
									Format attendu : SIREN (9 chiffres) ou PDL
									(14 chiffres)
								</p>
							</div>

							{/* 10. Poste (optionnel) */}
							<div className="gext-form__field">
								<label htmlFor="gext-poste">Poste</label>
								<input
									type="text"
									id="gext-poste"
									name="poste"
								/>
							</div>

							{/* 11. Checkbox CGV */}
							<div className="gext-form__submit_box">
								<div className="gext-form__checkboxe_field">
									<input
										type="checkbox"
										id="accepts_cgv"
										name="accepts_cgv"
										value="1"
										required
									/>
									<label htmlFor="accepts_cgv">
										J'autorise à stocker les informations
										renseignées afin de traiter ma demande
										en accord avec les{" "}
										{gextLiabilityForm.cgvUrl ? (
											<a
												href={gextLiabilityForm.cgvUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												Conditions Générales de Vente
											</a>
										) : (
											"Conditions Générales de Vente"
										)}
									</label>
								</div>

								<div className="gext-form__submit">
									<button type="submit" disabled={isLoading}>
										{isLoading
											? "Envoi en cours..."
											: "Envoyer"}
									</button>
								</div>
							</div>
						</div>
					</form>
				</>
			)}
			{isSuccess && subjectTo && (
				<FormResult
					variant="success"
					title="Vous êtes assujetti au Décret Tertiaire"
					descriptions={[
						"Vous avez l'obligation de déclarer tous les ans et de réduire significativement vos consommations d'énergie d'ici 2030.",
						<>
							<strong>Prochaines étapes :</strong> afin de vous
							accompagner dans votre démarche, demandez votre
							analyse énergétique et prenez rendez-vous avec l'un
							de nos experts.
						</>,
					]}
					actions={[
						...(gextLiabilityForm.analyseFormUrl
							? [
									{
										label: "Demander mon analyse énergétique",
										onClick: handleAnalyseFormClick,
										variant: "primary" as const,
									},
							  ]
							: []),
						...(gextLiabilityForm.appointmentUrl
							? [
									{
										label: "Prendre rendez-vous",
										onClick: handleAppointmentClick,
										variant: "secondary" as const,
									},
							  ]
							: []),
					]}
				/>
			)}
			{isSuccess && !subjectTo && (
				<FormResult
					variant="info"
					title={
						<>
							<div>Vous n'êtes pas assujetti au</div>
							<div>Décret Tertiaire</div>
						</>
					}
					descriptions={[
						"D'après les informations fournies, votre établissement ne répond pas aux critères pour être assujetti aux obligations du Décret Tertiaire. Cependant, cette première analyse est indicative et ne constitue pas un diagnostic réglementaire.",
						"En cas d'interrogation ou si votre situation évolue (agrandissement, changement d'activité...), nous restons à votre disposition pour répondre à vos questions.",
						<>
							<strong>Bon à savoir :</strong> Même hors
							assujettissement, optimiser votre consommation
							énergétique demeure un excellent investissement pour
							réduire durablement vos charges et votre impact
							environnemental.
						</>,
					]}
					actions={
						gextLiabilityForm.appointmentUrl
							? [
									{
										label: "Poser une question à un expert",
										onClick: handleAppointmentClick,
										variant: "secondary" as const,
									},
							  ]
							: []
					}
				/>
			)}
		</>
	);
}
